import {
  clearTriggerQueue,
  hoverRelease,
  runs,
  runStatus,
  selectedRun,
  triggerMessage,
  triggerQueue,
  undoLastRun,
} from '../state/avalanche.ts';
import { hoverProbe } from '../state/store.ts';
import { formatNumber } from './format.ts';
import { SpeedPresetButtons } from './SpeedPresetButtons.tsx';

const SIZE_WORDS: Record<1 | 2 | 3 | 4 | 5, string> = {
  1: 'small',
  2: 'medium',
  3: 'large',
  4: 'very large',
  5: 'extreme',
};

/** Live "what would release here" line while hovering, from the same preview the trigger tool
 * uses (Scene3D computes it on every hover tick in simple mode too). */
function HoverPreviewLine() {
  const probe = hoverProbe.value;
  const rel = hoverRelease.value;
  if (!probe || !rel) return null;
  if (rel.cells.length === 0) {
    return <p class="hint">Stable here — nothing would release.</p>;
  }
  return (
    <p class="hint">
      Size {rel.sizeClass} · {formatNumber(rel.volumeM3)} m³
    </p>
  );
}

function StatusSection() {
  const status = runStatus.value;
  const queue = triggerQueue.value;
  const msg = triggerMessage.value;
  return (
    <>
      {status.kind === 'running' ? (
        <div class="scenario-status">
          <progress />
          <span>Avalanche running… t = {formatNumber(status.simTime, 1)} s</span>
        </div>
      ) : null}
      {status.kind === 'error' ? (
        <div class="scenario-status error">Avalanche failed: {status.message}</div>
      ) : null}
      {queue.length > 0 ? (
        <div class="scenario-status">
          <span>{queue.length} more waiting</span>
          <button type="button" class="icon-button" onClick={clearTriggerQueue}>
            Clear queue
          </button>
        </div>
      ) : null}
      {msg ? <p class="warning">{msg}</p> : null}
    </>
  );
}

/** Latest avalanche's headline numbers — no α/β, particle stats or mass-balance table. */
function ResultCard() {
  const run = selectedRun.value;
  if (!run) return null;
  const s = run.result.stats;
  const busy = runStatus.value.kind === 'running';
  return (
    <section class="panel-section">
      <h3 class="segmented-heading">Result</h3>
      <dl class="info-list">
        <dt>EAWS size</dt>
        <dd>
          {s.sizeClass} ({SIZE_WORDS[s.sizeClass]})
        </dd>
        <dt>Debris volume</dt>
        <dd>{formatNumber(s.depositVolumeM3)} m³</dd>
        <dt>Debris mass</dt>
        <dd>{formatNumber(s.depositedKg / 1000, 1)} t</dd>
        {s.detrainedKg > 0 ? (
          <>
            <dt>Held back by forest</dt>
            <dd>{formatNumber(s.detrainedKg / 1000, 1)} t</dd>
          </>
        ) : null}
        {s.runout ? (
          <>
            <dt>Runout reach</dt>
            <dd>{formatNumber(s.runout.reach)} m</dd>
            <dt>Fall height</dt>
            <dd>{formatNumber(s.runout.fallHeight)} m</dd>
          </>
        ) : null}
        <dt>Max speed</dt>
        <dd>{formatNumber(s.maxSpeed, 1)} m/s</dd>
      </dl>
      <div class="field-row">
        <span class="hint">{formatNumber(runs.value.length)} avalanches</span>
        <button type="button" class="icon-button" onClick={undoLastRun} disabled={busy}>
          Undo last avalanche
        </button>
      </div>
    </section>
  );
}

/** Simple mode's "Avalanches" section: click-to-release instructions, a live hover preview,
 * run status/queue, playback speed presets and a short result card for the latest run. */
export function SimpleAvalanchePanel() {
  return (
    <>
      <section class="panel-section">
        <h2>Avalanches</h2>
        <p class="hint">
          Click the terrain to set off an avalanche — the snowpack decides how big it gets.
        </p>
        <HoverPreviewLine />
        <StatusSection />
        <div class="field-row">
          <span class="field-label">Playback speed</span>
        </div>
        <SpeedPresetButtons />
      </section>
      <ResultCard />
    </>
  );
}
